'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface MobileNavItem {
  label: string;
  href: string;
  icon: string;
}

const mobileNavItems: MobileNavItem[] = [
  { label: 'Home', href: '/dashboard', icon: 'HomeIcon' },
  { label: 'Prayers', href: '/prayer-history', icon: 'BookOpenIcon' },
  { label: 'Groups', href: '/prayer-groups', icon: 'UserGroupIcon' },
  { label: 'Community', href: '/community', icon: 'UsersIcon' },
  { label: 'Messages', href: '/messages', icon: 'ChatBubbleLeftRightIcon' },
];

interface MobileNavProps {
  activeRoute?: string;
}

export default function MobileNav({ activeRoute }: MobileNavProps) {
  const pathname = usePathname();
  const currentRoute = activeRoute || pathname;
  const navRef = useRef<HTMLElement>(null);

  // Haptic feedback on tap (Android/iOS PWA)
  const handleTap = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  };

  // Safe area inset for notched/gesture-bar devices
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    // Ensure nav sits above the home indicator on iOS/Android
    const safeBottom = getComputedStyle(document.documentElement)
      .getPropertyValue('--sab') || '0px';
    nav.style.paddingBottom = `calc(0.5rem + env(safe-area-inset-bottom, 0px))`;
  }, []);

  return (
    <nav
      ref={navRef}
      className="bg-card border-t border-border px-2 pt-2 flex items-center justify-around"
      style={{
        paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))',
        WebkitBackdropFilter: 'blur(20px)',
        backdropFilter: 'blur(20px)',
      }}
    >
      {mobileNavItems.map((item) => {
        const isActive =
          currentRoute === item.href ||
          (item.href !== '/dashboard' && currentRoute.startsWith(item.href));
        return (
          <Link
            key={`mobile-nav-${item.href}`}
            href={item.href}
            onClick={handleTap}
            className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all duration-150 active:scale-90 select-none ${
              isActive
                ? 'text-primary' :'text-muted-foreground active:text-primary'
            }`}
            style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
          >
            <span
              className={`flex items-center justify-center w-10 h-6 rounded-full transition-all duration-200 ${
                isActive ? 'bg-primary/15' : ''
              }`}
            >
              <Icon
                name={item.icon as Parameters<typeof Icon>[0]['name']}
                size={22}
                className={isActive ? 'text-primary' : 'text-muted-foreground'}
              />
            </span>
            <span
              className={`text-xs font-medium transition-all duration-200 ${
                isActive ? 'text-primary font-semibold' : 'text-muted-foreground'
              }`}
            >
              {item.label}
            </span>
          </Link>
        );
      })}
    </nav>
  );
}